/**
 * Pure helpers for a group's card on "My Groups": how the group's spend compares with its allowance,
 * where it is heading, and the status that sums that up. "Today" comes from the response's
 * `generatedAt`, never the real clock, so the figures agree with the charts and stay stable under test.
 */

import type { GroupDailySpendSeriesResponse } from "../../api/organisationReporting";
import { computeUsagePace, todayKeyFor } from "../consumption/usagePace";

/** How a group stands against its allowance. `none` when it has no allowance to stand against. */
export type GroupStatus = "onTrack" | "close" | "heading" | "over" | "within" | "none";

/** A projection within this share of the allowance counts as close to it. */
const CloseShare = 0.9;

export interface GroupPace {
  spend: number;
  allowance: number | null;
  projected: number | null;
  /** Spend so far as a percentage of the allowance (unrounded, may pass 100). */
  percentUsed: number | null;
  /** The projection as a percentage of the allowance (unrounded, may pass 100). */
  percentProjected: number | null;
  /** How far through the period today is, 0 to 100. */
  percentElapsed: number;
  /** What is left of the allowance per remaining working day, or null with no allowance or past month. */
  headroomPerWorkingDay: number | null;
  remainingWorkingDays: number;
  status: GroupStatus;
  /** How far over the allowance the projection (current month) or the spend (past month) is, as a whole percentage. */
  overPercent: number;
  /** Spend per person in the group, or null for a group with no people. */
  perPerson: number | null;
}

/**
 * Works out a group's pace for one period.
 *
 * @param spend - The group's spend so far.
 * @param allowance - The group's monthly allowance in the same currency, or null when it has none.
 * @param projected - The server's projected period total, or null (a past month, or no projection).
 * @param personCount - How many people the group holds.
 * @param period - The period and the instant the figures were generated.
 * @param isCurrentMonth - Whether the period is the current month.
 */
export function computeGroupPace(
  spend: number,
  allowance: number | null,
  projected: number | null,
  personCount: number,
  period: { periodStart: string; periodEnd: string; generatedAt: string },
  isCurrentMonth: boolean,
): GroupPace {
  const usable = allowance !== null && allowance > 0 ? allowance : null;
  const base = computeUsagePace({ ...period, amount: spend, weekdayRatePerDay: 0, weekendRatePerDay: 0 }, usable ?? 1);
  const percentElapsed = isCurrentMonth ? base.percentElapsed : 100;
  const percentProjected = usable !== null && projected !== null ? (projected / usable) * 100 : null;

  let status: GroupStatus = "none";
  let overPercent = 0;
  if (usable !== null) {
    if (spend > usable) {
      status = "over";
      overPercent = Math.round(((spend - usable) / usable) * 100);
    } else if (!isCurrentMonth) {
      status = "within";
    } else if (projected !== null && projected > usable) {
      status = "heading";
      overPercent = Math.round(((projected - usable) / usable) * 100);
    } else if (projected !== null && projected > usable * CloseShare) {
      status = "close";
    } else {
      status = "onTrack";
    }
  }

  return {
    spend,
    allowance: usable,
    projected,
    percentUsed: usable !== null ? (spend / usable) * 100 : null,
    percentProjected,
    percentElapsed,
    headroomPerWorkingDay: usable !== null && isCurrentMonth ? base.headroomPerWorkingDay : null,
    remainingWorkingDays: base.remainingWorkingDays,
    status,
    overPercent,
    perPerson: personCount > 0 ? spend / personCount : null,
  };
}

/** One day of a burn-up chart: spend so far (to today), the projection (from today) and the allowance. */
export interface BurnUpPoint {
  date: string;
  actual: number | null;
  projected: number | null;
}

/**
 * The running total of a group's daily spend up to today, followed by the server's projection to the
 * period end. The projection starts at today, so the two lines meet.
 *
 * @param series - The group's daily spend series.
 * @param periodStart - The first day of the period.
 * @param periodEnd - The last day of the period.
 * @param generatedAt - The instant the figures were generated, treated as now.
 */
export function burnUpPoints(series: GroupDailySpendSeriesResponse, periodStart: string, periodEnd: string, generatedAt: string): BurnUpPoint[] {
  const today = todayKeyFor(periodStart, periodEnd, generatedAt);
  const projectedByDate = new Map((series.projection?.points ?? []).map((point) => [point.date, point.cumulativeAmount]));
  // The days are usage alone, so the running total starts at the period's seat fees.
  let running = series.seatFees ?? 0;
  return series.days.map((day) => {
    const reached = day.date <= today;
    if (reached) {
      running += day.totalAmount;
    }
    return { date: day.date, actual: reached ? running : null, projected: projectedByDate.get(day.date) ?? null };
  });
}
